import { createFileRoute } from '@tanstack/react-router';
import { Check } from "lucide-react";
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/features/sso-saml')({
  component: SSOSAMLPage,
});

const features = [
  {
    iconName: "Key",
    title: "SAML 2.0",
    description: "Full SAML 2.0 support for enterprise identity providers. SP and IdP initiated flows.",
  },
  {
    iconName: "Shield",
    title: "OIDC / OAuth 2.0",
    description: "OpenID Connect for modern identity providers and custom OAuth integrations.",
  },
  {
    iconName: "Users",
    title: "SCIM Provisioning",
    description: "Automatic user provisioning and deprovisioning synced with your identity provider.",
  },
  {
    iconName: "RefreshCw",
    title: "Directory Sync",
    description: "Sync users, groups, and permissions from your corporate directory automatically.",
  },
  {
    iconName: "Settings",
    title: "Custom Domains",
    description: "Use your own domain for SSO. Users log in at login.yourcompany.com.",
  },
  {
    iconName: "CheckCircle",
    title: "MFA Support",
    description: "Supports multi-factor authentication enforced by your identity provider.",
  },
];

const providers = [
  "Okta",
  "Azure Active Directory",
  "Google Workspace",
  "OneLogin",
  "Ping Identity",
  "Auth0",
  "JumpCloud",
  "Duo Security",
];

const capabilities = [
  "SP-initiated and IdP-initiated SSO",
  "Just-in-time user provisioning",
  "Group-based role assignment",
  "Automatic user deactivation",
  "Session management and timeout",
  "Audit logging for all auth events",
  "Custom attribute mapping",
  "Multiple IdP support",
];

const faqItems = [
  {
    question: "Which identity providers do you support?",
    answer: "We support any SAML 2.0 or OIDC compliant identity provider, including Okta, Azure AD, Google Workspace, OneLogin, Ping Identity, Auth0, and many more.",
  },
  {
    question: "How does SCIM provisioning work?",
    answer: "SCIM automatically syncs users from your identity provider. When you add or remove users in Okta (for example), those changes automatically reflect in Matricks. Group memberships can also be synced.",
  },
  {
    question: "Can we enforce MFA?",
    answer: "Yes, MFA is enforced by your identity provider. When users authenticate via SSO, they go through your IdP's MFA flow. We respect and trust your IdP's authentication decisions.",
  },
  {
    question: "What about users not in our IdP?",
    answer: "You can configure Matricks to allow only SSO users (enforce SSO) or allow a mix of SSO and email/password users. Guest users and external collaborators can be handled separately.",
  },
];

function SSOSAMLPage() {
  return (
    <div>
      <ContentHeader
        title="SSO & SAML"
        description="Enterprise-grade authentication for your organization. Connect with your existing identity provider for seamless, secure access to Matricks."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Features", href: "/features" },
          { label: "SSO & SAML" },
        ]}
        badge="Feature"
      />

      {/* Features */}
      <ContentSection
        id="features"
        title="Authentication Features"
        background="muted"
      >
        <FeatureShowcase features={features} columns={3} variant="cards" />
      </ContentSection>

      {/* Providers */}
      <ContentSection
        id="providers"
        title="Supported Identity Providers"
        description="Works with all major enterprise identity providers."
      >
        <div className="flex flex-wrap gap-4 justify-center">
          {providers.map((provider) => (
            <div
              key={provider}
              className="rounded-lg border bg-card px-6 py-3 text-sm font-medium"
            >
              {provider}
            </div>
          ))}
        </div>
        <p className="text-center text-muted-foreground mt-6">
          Plus any SAML 2.0 or OIDC compliant provider
        </p>
      </ContentSection>

      {/* How It Works */}
      <ContentSection
        id="how-it-works"
        title="How SSO Works"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
              1
            </div>
            <h3 className="mt-4 font-semibold">User Visits Matricks</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              User navigates to login or your custom domain.
            </p>
          </div>
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
              2
            </div>
            <h3 className="mt-4 font-semibold">Redirected to IdP</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              User is redirected to your identity provider.
            </p>
          </div>
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
              3
            </div>
            <h3 className="mt-4 font-semibold">Authenticate</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              User authenticates with your IdP (including MFA).
            </p>
          </div>
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
              4
            </div>
            <h3 className="mt-4 font-semibold">Access Granted</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              User is logged into Matricks with their assigned roles.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* Capabilities */}
      <ContentSection id="capabilities" title="Full Capabilities">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {capabilities.map((capability) => (
            <div key={capability} className="flex items-center gap-3">
              <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
              <span>{capability}</span>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Secure Your Team's Access"
        description="Enable SSO for your organization and simplify authentication while improving security."
        primaryAction={{ label: "Contact Sales", href: "/contact" }}
        secondaryAction={{ label: "View Pricing", href: "/pricing" }}
        background="primary"
      />
    </div>
  );
}
